import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Package, CheckCircle2, Handshake, ArrowLeft, Star, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReviewModal from '@/components/ReviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TradesPage() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [listings, setListings] = useState({});
  const [bids, setBids] = useState({});
  const [users, setUsers] = useState({});
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ open: false, trade: null });

  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/trades/my-trades`);
      setTrades(response.data);

      const listingIds = [...new Set(response.data.map((trade) => trade.listing_id))];
      const bidIds = [...new Set(response.data.map((trade) => trade.winning_bid_id))];

      // Fetch listings
      const listingPromises = listingIds.map((id) => 
        axios.get(`${API_URL}/api/listings/${id}`).catch(() => null)
      );
      const listingResponses = await Promise.all(listingPromises);
      const listingsMap = {};
      listingResponses.forEach((res) => {
        if (res?.data) {
          listingsMap[res.data.id] = res.data;
        }
      });
      setListings(listingsMap);

      // Fetch bids by fetching bids for each listing and finding the winning bid
      const bidsMap = {};
      for (const trade of response.data) {
        try {
          const bidRes = await axios.get(`${API_URL}/api/bids/listing/${trade.listing_id}`);
          const winningBid = bidRes.data.find(b => b.id === trade.winning_bid_id);
          if (winningBid) {
            bidsMap[winningBid.id] = winningBid;
          }
        } catch (err) {
          console.error('Failed to fetch bid', err);
        }
      }
      setBids(bidsMap);

      // Fetch user info for other parties
      const userIds = [...new Set(response.data.flatMap(t => [t.seller_id, t.buyer_id]))];
      const usersMap = {};
      for (const userId of userIds) {
        try {
          const userRes = await axios.get(`${API_URL}/api/users/${userId}`);
          usersMap[userId] = userRes.data;
        } catch (err) {
          console.warn(`Failed to fetch user ${userId}:`, err);
        }
      }
      setUsers(usersMap);

      // Check which trades user has already reviewed
      const reviewsMap = {};
      for (const trade of response.data) {
        if (trade.status === 'completed') {
          try {
            // Check if user has reviewed this trade
            const reviewsRes = await axios.get(`${API_URL}/api/reviews/user/${trade.seller_id === user.id ? trade.buyer_id : trade.seller_id}`);
            const myReview = reviewsRes.data.find(r => r.trade_id === trade.id && r.reviewer_id === user.id);
            if (myReview) {
              reviewsMap[trade.id] = myReview;
            }
          } catch (err) {
            console.warn(`Failed to fetch reviews for trade ${trade.id}:`, err);
          }
        }
      }
      setReviews(reviewsMap);
    } catch (error) {
      console.error('Failed to fetch trades', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (tradeId) => {
    try {
      await axios.patch(`${API_URL}/api/trades/${tradeId}/confirm`);
      toast.success('Trade confirmed!');
      fetchTrades();
    } catch (error) {
      toast.error('Failed to confirm trade');
    }
  };

  const handleComplete = async (tradeId) => {
    try {
      await axios.patch(`${API_URL}/api/trades/${tradeId}/complete`);
      toast.success('Trade completed!');
      fetchTrades();
    } catch (error) {
      toast.error('Failed to complete trade');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'initiated':
        return 'bg-blue-100 text-blue-700';
      case 'confirmed':
        return 'bg-green-100 text-green-700';
      case 'completed':
        return 'bg-indigo-100 text-indigo-700';
      case 'disputed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-indigo-600 mb-6 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Home</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            My Trades
          </h1>
          <p className="text-slate-600">Manage your active and completed trades</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : trades.length === 0 ? (
          <div className="text-center py-20">
            <Handshake className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No trades yet</h3>
            <p className="text-slate-600 mb-6">Trades will appear here once bids are accepted</p>
            <Link
              to="/"
              className="inline-block rounded-full font-bold px-8 py-3 bg-indigo-600 text-white hover:bg-indigo-700 transition-all"
              data-testid="browse-to-trade-button"
            >
              Browse Listings
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {trades.map((trade) => {
              const listing = listings[trade.listing_id];
              const bid = bids[trade.winning_bid_id];
              const isSeller = listing?.user_id === trade.seller_id;

              return (
                <div
                  key={trade.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6"
                  data-testid={`trade-card-${trade.id}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <img src="/logo.png" alt="Trade" className="w-10 h-10 rounded-lg" />
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{listing?.title || 'Loading...'}</h3>
                        <p className="text-sm text-slate-600">
                          Trade #{trade.id.slice(0, 8)}
                        </p>
                      </div>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(trade.status)}`}>
                      {trade.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">Trading</h4>
                      {listing?.images && listing.images.length > 0 && (
                        <img
                          src={listing.images[0]}
                          alt={listing.title}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      )}
                      <p className="text-sm text-slate-600">{listing?.description}</p>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">For</h4>
                      {bid?.items && bid.items.length > 0 ? (
                        <div className="space-y-2 mb-3">
                          {bid.items.map((item, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <Package className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm text-slate-700">{item.title}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {bid?.money_amount > 0 && (
                        <div className="text-lg font-bold text-lime-600">${bid.money_amount}</div>
                      )}
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-xl p-4 mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Trade Confirmation</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        {trade.seller_confirmed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-slate-300"></div>
                        )}
                        <span className="text-sm text-slate-700">Seller confirmed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {trade.buyer_confirmed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-slate-300"></div>
                        )}
                        <span className="text-sm text-slate-700">Buyer confirmed</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {trade.status === 'initiated' && (
                      <>
                        {(!trade.seller_confirmed && isSeller) || (!trade.buyer_confirmed && !isSeller) ? (
                          <Button
                            onClick={() => handleConfirm(trade.id)}
                            className="flex-1 rounded-full font-bold px-8 py-3 bg-lime-500 text-slate-900 hover:bg-lime-400"
                            data-testid={`confirm-trade-${trade.id}`}
                          >
                            Confirm Trade
                          </Button>
                        ) : (
                          <div className="flex-1 text-center py-3 text-slate-600">
                            Waiting for other party to confirm
                          </div>
                        )}
                      </>
                    )}
                    {trade.status === 'confirmed' && (
                      <Button
                        onClick={() => handleComplete(trade.id)}
                        className="flex-1 rounded-full font-bold px-8 py-3 bg-indigo-600 text-white hover:bg-indigo-700"
                        data-testid={`complete-trade-${trade.id}`}
                      >
                        Mark as Completed
                      </Button>
                    )}
                    {trade.status === 'completed' && (
                      <>
                        <div className="flex-1 text-center py-3 text-green-600 font-semibold">
                          Trade completed successfully!
                        </div>
                        {!reviews[trade.id] ? (
                          <Button
                            onClick={() => setReviewModal({ 
                              open: true, 
                              trade,
                              otherUserId: isSeller ? trade.buyer_id : trade.seller_id 
                            })}
                            className="rounded-full font-bold px-6 py-3 bg-amber-500 text-white hover:bg-amber-600"
                            data-testid={`review-trade-${trade.id}`}
                          >
                            <Star className="w-4 h-4 mr-2" />
                            Leave Review
                          </Button>
                        ) : (
                          <div className="flex items-center gap-1 text-amber-600 px-4">
                            <Star className="w-4 h-4 fill-amber-400" />
                            <span className="text-sm font-medium">Reviewed</span>
                          </div>
                        )}
                      </>
                    )}
                    {/* Message button - always visible */}
                    <Link
                      to={`/chat/${bid?.id}`}
                      className="rounded-full font-semibold px-6 py-3 bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-2 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Message
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewModal.trade && (
        <ReviewModal
          isOpen={reviewModal.open}
          onClose={() => setReviewModal({ open: false, trade: null })}
          tradeId={reviewModal.trade.id}
          otherUserName={users[reviewModal.otherUserId]?.name || 'User'}
          listingTitle={listings[reviewModal.trade.listing_id]?.title || 'Item'}
          onReviewSubmitted={fetchTrades}
        />
      )}
    </div>
  );
}
